import { randomUUID as uuidv4 } from 'expo-crypto';
import { ICartEntry } from 'features/cart/types';
import {
  mapMenuSelectionsToMinimalCartEntry,
  mapSelectedProductToMenuSelections,
} from 'features/cart/utils/minimal-cart';
import {
  initializeSelectedProduct,
  initializeSlotSelections,
} from 'features/menu/components/product-wizard/utils';

import { IBaseItem, ILegacyCartEntry } from '@rbi-ctg/menu';
import {
  IConfigOfferEntryDto,
  IRewardEntryDto,
  ISystemWideOfferEntryDto,
} from 'generated/graphql-gateway';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import { isCombo } from 'state/unified-menu/utils';
import { routes } from 'utils/routing';

export const itemIdExistsInEntries = (itemId: string, entries: ILegacyCartEntry[]): boolean =>
  entries.some(
    (entry: ILegacyCartEntry) =>
      itemId === entry._id ||
      (entry.children.length ? itemIdExistsInEntries(itemId, entry.children) : false)
  );

export const legacyItemExistsInEntries = (item: IBaseItem, entries: ILegacyCartEntry[]): boolean =>
  itemIdExistsInEntries(item._id, entries);

/**
 * Builds a One Menu upsell cart entry with default selections.
 * Always uses a fresh cartId and isUpsell — never reuses PDP cartIdEditing
 * (matches control FeatureMenu upsell for addItemToCart).
 */
export const buildOneMenuUpsellCartEntry = ({
  item,
  indexedMenu,
  indexedOffers,
  indexedRewards,
  posServiceMode,
}: {
  item: ILocalizedMenuEntry;
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>;
  indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto>;
  indexedRewards: IndexedMenu<IRewardEntryDto>;
  posServiceMode: PosServiceMode;
}): ICartEntry | null => {
  const selectedProduct =
    initializeSelectedProduct({
      menu: indexedMenu,
      item,
      posServiceMode,
    }) ?? item;
  const slotsSelections = initializeSlotSelections(
    selectedProduct,
    indexedMenu,
    isCombo(selectedProduct),
    posServiceMode
  );
  const menuSelections = mapSelectedProductToMenuSelections(selectedProduct, 1, slotsSelections);
  const url = `${routes.menu}/${selectedProduct.type.toLowerCase()}/${selectedProduct.id}`;
  const [mappedEntry] = mapMenuSelectionsToMinimalCartEntry(menuSelections, {
    menu: indexedMenu,
    indexedOffers,
    indexedRewards,
    posServiceMode,
    url,
  });

  if (!mappedEntry) {
    return null;
  }

  return {
    ...mappedEntry,
    cartId: uuidv4(),
    isUpsell: true,
  };
};
